import React from 'react';
import { IdentityPasswordComplexity } from 'appDtos';
import { connect } from 'react-redux';
import { Field, reduxForm, InjectedFormProps, getFormValues } from 'redux-form';
import { FormValues, FormErrors } from './set-password-requirements';
import { setPassword } from '../../api/products';
import {
  isConfirmPasswordValid,
  isFormPristine,
  isFormValid,
  isPasswordValid,
  getFormErrorsFromAppState,
  syncValidate,
} from './set-password-validate';
import { OnlineTranslationContext } from '@chr/online-shared-ui-localizations';

export interface SetPasswordFormProps {
    formValues: FormValues,
    formErrors: FormErrors,
    formPristine: boolean,
    formValid: boolean,
    passwordValid: boolean,
    confirmPasswordValid: boolean,
    email: string,
    firstName: string,
    lastName: string,
    passwordRequirements: IdentityPasswordComplexity,
    location: any,
    deactivatedIdentity: () => void
}

export interface SetPasswordFormState {
    showPassword: boolean,
    submitFailed: boolean,
  };

export class SetPasswordForm extends React.Component<SetPasswordFormProps & InjectedFormProps <{}, SetPasswordFormProps> ,SetPasswordFormState> {

    state: SetPasswordFormState = {
        showPassword: false,
        submitFailed: false,
    }

    static contextType = OnlineTranslationContext;
    translate = (key: string, replaceVariable?: string[]) => this.context.translate ? this.context.translate(key, replaceVariable): key;

    submitForm = async () => {
      const passwordValue = this.props.formValues.password;
      const { search } = this.props.location;
      const params = new URLSearchParams(search);
      const invitationId = parseInt(params.get('invitationId') as string);
      const token = params.get('token') as string;
      try {
        await setPassword(invitationId, token, passwordValue as string);
      } catch (error: any) {
        if (error.response?.status === 400) {
          this.props.deactivatedIdentity();
        }
        this.setState({ submitFailed: true});
      }
    }

    toggleShowPassword = () => {
      const showPassword = !this.state.showPassword;
      this.setState({ showPassword });
    }

    renderField = ({ id, label, input, type, isDisabled, meta: { touched, error } }: any) => {
      return (
        <div className={`form-group ${touched && error ? 'has-error' : ''}`}>
            <label htmlFor={id} className="required">{label}</label>
            <input {...input} id={id} type={type} className="form-control" disabled={isDisabled} data-test={id} />
            {touched && error && <span className="help-text ns-icon ns-error space-outer-top-sm">{this.translate(error.message)}</span>}
        </div>
      )
    }

    formSubmitErrorMessage = (
      <div className="form-error-container d-flex" role="alert" data-test="submit-error-message">
        <div className="ns-icon ns-error"></div>
        <div>
          <p>{this.translate("There was a problem. Your new account could not be activated.")}</p>
          <p>{this.translate("Please go back to your invitation email and click the account activation link again.")}</p>
          <p>{this.translate("If the problem continues, please contact your C.H. Robinson representative.")}</p>
        </div>
      </div>
    );

    render() {
      const { passwordValid, formPristine, handleSubmit, submitting, submitFailed, formValid} = this.props;
      const inputType = this.state.showPassword ? 'text' : 'password';
      const isConfirmPasswordDisabled = !passwordValid || formPristine;

      return (
        <div className="user-activation-form-container">
          <form className="user-activation-form" onSubmit={handleSubmit(this.submitForm)}>
            <Field
              id="password"
              name="password"
              component={this.renderField}
              type={inputType}
              label={this.translate("Password")}
              isDisabled={submitting || submitFailed}
              key="password"
            />
            <Field
              id="confirmPassword"
              name="confirmPassword"
              component={this.renderField}
              type={inputType}
              label={this.translate("Confirm Password")}
              isDisabled={submitting || submitFailed || isConfirmPasswordDisabled}
              key="confirmPassword"
            />
            <div className="text-right">
              <label htmlFor="showPassword" className="space-outer-right-sm text-normal">{this.translate("Show Password")}</label>
              <input id="showPassword" type="checkbox" checked={this.state.showPassword} onChange={this.toggleShowPassword} data-test="showPassword"/>
            </div>
            {submitFailed ? this.formSubmitErrorMessage : null}
            <button
              className="btn btn-primary space-outer-top-lg pull-right"
              type="submit"
              disabled={submitting || submitFailed || !formValid}
              data-test="submitButton"
            >{submitting ? <span className="glyphicon glyphicon-repeat spinning"></span> : this.translate("Submit")}</button>
          </form>
        </div>
      )
    }
}

export const mapStateToProps = (state: AppState) => {
    return {
      formPristine: isFormPristine(state),
      formValues: getFormValues('setPasswordForm')(state) as {password: string; confirmPassword: string;},
      formErrors: getFormErrorsFromAppState(state),
      formValid: isFormValid(state),
      passwordValid: isPasswordValid(state),
      confirmPasswordValid: isConfirmPasswordValid(state),
    }
};

export const form = reduxForm<{}, SetPasswordFormProps>({
  form: 'setPasswordForm',
  validate: syncValidate,
  initialValues: { password: '', confirmPassword: '' },
});

export const ConnectedSetPasswordForm = connect(mapStateToProps)(form(SetPasswordForm));

